import type { SchoolTestDomain } from '@mathflat/domain/@entities/(Content)/SchoolTest/domain'
import type { CurriculumApi } from '@mathflat/domain/@entities/Curriculum/api'
import type { RegionDomain } from '@mathflat/domain/@entities/Region/domain'
import type { AllGradeSubject, Semester } from '@mathflat/domain/@entities/SchoolSystem/schema'
import QueryString from 'qs'

import type { SchoolGradeData } from '@/api/studentHistory.api.ts'
import type { DownloadWorksheetT } from '@/contentComponents/modal/Download/WorksheetDownload.service'
import type { LessonClass, Worksheet } from '@/dto'
import type { DefaultResponse, Pagination } from '@/types/common'
import type { PaginationResponse } from '@/types/common/api'
import maxios, { type CustomRequestConfigMeta } from '@/utils/axios/maxios'

// 학습지 API
const worksheetApi = {
  // 학습지 디자인 템플릿 가져오기 - 사이언스플랫 사용
  getDesignTemplate: () => {
    return maxios.get<res.WorksheetDesignTemplate[]>(`/worksheet/design/types`)
  },

  /* 학습지 만들기 */
  // 학습지 만들기 - 1단계 : 단원유형별 학습지 필터 생성 - filter filterId 생성 - 사이언스플랫 사용
  createFilterIdByChapter: (data: req.CreateFilterByChapter, meta?: CustomRequestConfigMeta) => {
    return maxios.post<{ filterId: string }>('/worksheets/filter/concept', data, {
      meta,
    })
  },

  // 학습지 만들기 - 검색된 결과 가져오기 : filterId를 통한 학습지 옵션 검색 - 사이언스플랫 사용
  getSearchFilterByFilterId: (filterId: string, meta?: CustomRequestConfigMeta) => {
    return maxios.get<res.SearchFilterByChapterFilterId>(`/worksheets/filter`, {
      params: {
        filterId,
      },
      meta,
    })
  },

  // 학습지만들기 - 1단계 : 수능 모의고사 필터 전달 - filterId 생성
  createFilterByPracticeTest: (data: req.CreateFilterByPracticeTest) => {
    return maxios.post<DefaultResponse<{ filterId: string }>>('/worksheet/filter/practice', data)
  },
  //TODO: [리팩토링] updateFilterByPracticeTest : createFilterByPracticeTest에서 params에 filterId가 존재하면 update임. type과 함수로 구분하기. 모든 filterId생성 함수 동일하게 다 고쳐야.

  // 오답학습지 - 단원별 filterID 만들기
  createFilterIdByConcept: (data: req.CreateFilterByWeakConcept) => {
    return maxios.post<DefaultResponse<{ filterId: string }>>(
      '/worksheet/filter/weak-concept',
      data,
    )
  },

  // 오답학습지 - 기간별 filterID 만들기
  createFilterIdByPeriod: (data: req.CreateFilterByWeakPeriod) => {
    return maxios.post<DefaultResponse<{ filterId: string }>>('/worksheet/filter/weak-period', data)
  },

  // 오답학습지 - 학습지별 filterID 만들기 - 사이언스플랫 사용
  createFilterIdByWorksheet: (data: req.CreateFilterByWeakWorksheet) => {
    return maxios.post<{ filterId: string }>('/worksheets/filter/weak-worksheet', data)
  },

  // 원클릭학습지 - filterId 만들기
  createFilterIdByOneClick: (data: req.CreateFilterByOneClick) => {
    return maxios.post<DefaultResponse<{ filterId: string }>>('/worksheet/filter/one-click', data)
  },

  // 학습지 병합 - filterId 만들기
  postFilterIdByMergedWorksheet: (data: req.CreateFilterByMerge) => {
    return maxios.post<res.FilterId>('/worksheet/filter/merged-worksheet', data)
  },

  postGroupWrongWorksheetPerStudent: (data: req.CreateEachStudentWorksheet) => {
    return maxios.post<DefaultResponse<Worksheet['id']>>('/worksheets/group', data)
  },

  // 학습지 만들기 - 유사문제 검색 - 사이언스플랫 사용
  postSearchWorksheetSimilarProblem: (
    data: req.SearchSimilarProblem,
    meta?: CustomRequestConfigMeta,
  ) => {
    return maxios.post<res.MakeWorksheetProblem[]>(`/worksheets/problem`, data, {
      meta,
    })
  },
  // 학습지 만들기 - 1단계 : filterId를 통해 학습지 문제 수 조회 - 사이언스플랫 사용
  getProblemCountByFilterId: (params: { filterId: string }, meta?: CustomRequestConfigMeta) => {
    return maxios.get<res.ProblemCountByFilterId>(`/worksheets/problem/count`, {
      params,
      meta,
    })
  },
  // 학습지 만들기 - 3단계 : 학습지 최종 생성하기 - 복제후 수정 - 사이언스플랫 사용
  createWorksheet: (data: req.CreateWorksheet, meta?: CustomRequestConfigMeta) => {
    return maxios.post<DefaultResponse<Worksheet['id']>>('/worksheets', data, {
      meta,
    })
  },
  // 학습지 만들기 - 학습지 수정하기 - 사이언스플랫 사용
  putWorksheetFix: (worksheetId: number, data: req.CreateWorksheet) => {
    return maxios.put<DefaultResponse<null>>(`/worksheets/${worksheetId}`, data)
  },
  // 학습지 만들기 - 출제된 학습지 수정하기
  patchWorksheetFix: (worksheetId: number, data: req.EditAssignedWorksheet) => {
    return maxios.patch<DefaultResponse<null>>(`/worksheets/${worksheetId}`, data)
  },
  /* 학습지 디자인 */
  // 학습지 디자인 - 학습진 디자인 리스트 가져오기 - 사이언스플랫 사용
  getWorksheetDesign: (queryString: string) => {
    return maxios.get<{ content: res.WorksheetDesign[]; last: boolean }>(
      `/worksheet/design${queryString}`,
    )
  },
  // 학습지 디자인 - 학습지 디자인 가져오기 - 사이언스플랫 사용
  getWorksheetDesignDetail: (id: number) => {
    return maxios.get<res.WorksheetDesignDetail>(`/worksheet/design/${id}`)
  },
  // 학습지 디자인 - 학습지 디자인 생성 - 사이언스플랫 사용
  postWorksheetDesignDetail: (data: req.WorksheetDesignDetail) => {
    return maxios.post(`/worksheet/design`, data)
  },
  // 학습지 디자인 - 학습지 디자인 수정 - 사이언스플랫 사용
  putWorksheetDesignDetail: (id, data: req.WorksheetDesignDetail) => {
    return maxios.put(`/worksheet/design/${id}`, data)
  },
  // 학습지 디자인 -학습지 디자인 삭제 - 사이언스플랫 사용
  deleteWorksheetDesignDetail: (id: number) => {
    return maxios.delete(`/worksheet/design/${id}`)
  },
  // 기관 학습지 가져오기 - 사이언스플랫 사용
  getMyWorksheet: (params: string) => {
    return maxios.get<AcademyResponse>(`/worksheets/academy${params}`)
  },
  // 연산 학습지 가져오기
  getLegacyArithmetic: (params: req.LegacyArithmetic) => {
    return maxios.get<LegacyArithmeticResponse>(`/worksheet/arithmetic`, {
      params: {
        ...params,
      },
    })
  },
  // 학습지 조회 - 사이언스플랫 사용
  getWorksheetDetail: <T extends 'WEAK_WORKSHEET' | 'WORKSHEET' = 'WORKSHEET'>(
    worksheetId: number,
    ignoredForDeleted?: boolean,
  ) => {
    const params = QueryString.stringify(
      {
        ignoredForDeleted,
      },
      { addQueryPrefix: true },
    )

    return maxios.get<res.WorksheetDetail<T>>(`/worksheet/${worksheetId}${params}`)
  },
  getAssignedStudentInfoByWorksheetId: (params: req.AssignedStudentsByWorksheetIdParams) => {
    return maxios.get<DefaultResponse<res.AssignedStudents>>(`/worksheet/${params.id}/assign`)
  },

  getWorksheetDownload: (
    params: req.WorksheetDownloadParams,
    type?: DownloadWorksheetT['type'],
  ) => {
    return maxios.get<common.PresignedFile>(`/worksheets/v2/download`, {
      params: {
        ...params,
      },
    })
  },
  deleteWorksheets: (worksheetIds: number[]) => {
    return maxios({
      url: '/worksheets',
      method: 'DELETE',
      data: worksheetIds,
    })
  },
  getChapters: async (params: req.CurriculumChapters) => {
    return maxios.get<DefaultResponse<CurriculumApi.Response.CurriculumInfo[]>>(
      `/curriculums/nested`,
      {
        params: { ...params },
      },
    )
  },
  getArithmeticWorksheetsByCurriculumKey: async (params: req.CurriculumGroups) => {
    return maxios.get<Pagination<res.WorksheetsByCurriculumKey>>(`/worksheet/arithmetic/group`, {
      params: {
        ...params,
      },
    })
  },
  patchAccessModifier: async (requestBody: req.AccessModifier) => {
    return maxios.patch(`/worksheet`, requestBody)
  },
  getGradeXLS: async (
    { schoolType, grade }: SchoolGradeData,
    dateParams: req.DownloadHistoryParams,
  ) => {
    return maxios.get(`/student-worksheets/grade/${schoolType}/${grade}/xlsx`, {
      responseType: 'blob',
      params: { ...dateParams },
    })
  },
  getClassXLS: async (classId: LessonClass['id'], dateParams: req.DownloadHistoryParams) => {
    return maxios.get(`/student-worksheets/lesson-class/${classId}/xlsx`, {
      responseType: 'blob',
      params: { ...dateParams },
    })
  },
}

export default worksheetApi

type AcademyResponse = {
  content: res.MyWorksheet[]
  last: boolean
  first: boolean
  size: number
  number: number
  numberOfElements: number
  empty: boolean
}
type LegacyArithmeticResponse = Pagination<res.LegacyArithmeticWorksheet[]>

export type SimilarWorksheetBySchoolTestResponse = SimilarWorksheetBySchoolTest[]
type SimilarWorksheetCountBySchoolTestResponse = {
  sameSchool: number
  sameRegion: number
  neighborhoodRegion: number
  specialEducationRegion: number
}

export type SchoolTestSearchType = 'SCHOOL' | 'REGION' | 'NEIGHBORHOOD' | 'SPECIAL_EDUCATION'

export type SimilarWorksheetBySchoolTest = {
  id: number
  title: string
  chapter: string
  year: number
  grade: AllGradeSubject
  semester: Semester
  schoolTestType: SchoolTestDomain.SchoolTestType
  school: RegionDomain.SchoolByRegion
  region: RegionDomain.Region
  assignList: _AssignedStudentWorksheet[]
}
